import { ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Permission, UserRole } from '../types/auth'

interface ProtectedRouteProps {
  children: ReactNode
  requiredPermission?: Permission
  requiredRole?: UserRole
  fallback?: ReactNode
}

export function ProtectedRoute({ 
  children, 
  requiredPermission, 
  requiredRole,
  fallback 
}: ProtectedRouteProps) {
  const { isAuthenticated, user, hasPermission, needsOnboarding, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    window.location.href = '/login'
    return null
  }

  // If user needs onboarding, redirect to onboarding flow
  if (needsOnboarding) {
    window.location.href = '/onboarding'
    return null
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || <UnauthorizedAccess />
  }

  if (requiredRole && user?.role !== requiredRole) {
    return fallback || <UnauthorizedAccess />
  }

  return <>{children}</>
}

function UnauthorizedAccess() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">
          You don't have permission to access this page.
        </p>
      </div>
    </div>
  )
}